import { Link, useParams } from 'react-router';

import { CreateMasterProjectButton } from 'features/master';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { Skeleton } from '../../../components/ui/skeleton';
import { useGetProjectsQuery } from '../api/projects';
import { getMasterProjects, getSubProjects } from '../model/projectTree';

const ProjectsView = () => {
  const { masterProjectId } = useParams();
  const routeMasterId = masterProjectId !== undefined ? Number(masterProjectId) : null;
  const { data: projects = [], isLoading } = useGetProjectsQuery();
  const masterProject =
    routeMasterId !== null && Number.isFinite(routeMasterId)
      ? getMasterProjects(projects).find((p) => p.id === routeMasterId) ?? null
      : null;
  const subProjects = getSubProjects(projects, masterProject?.id ?? null);
  const showEmptyState = routeMasterId === null || !Number.isFinite(routeMasterId) || masterProject === null;

  if (isLoading) {
    return (
      <section className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-48" />
        </header>
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (showEmptyState) {
    return (
      <section className="flex flex-col gap-6">
        <header>
          <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">Configuración</p>
          <h2 className="text-2xl font-bold text-text-default m-0 font-display">Proyectos</h2>
        </header>
        <div className="bg-bg-surface border border-border-subtle rounded-xl p-8 flex flex-col items-center gap-4 text-center">
          <p className="text-text-muted text-sm max-w-md">
            Selecciona una inmobiliaria en el sidebar para ver sus subproyectos, o crea una nueva inmobiliaria.
          </p>
          <CreateMasterProjectButton />
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">Inmobiliaria</p>
          <h2 className="text-2xl font-bold text-text-default m-0 font-display">
            {masterProject.name} <span className="text-text-muted text-xs font-normal">({masterProject.id})</span>
          </h2>
        </div>
        <Button asChild size="sm" className="gap-2 shrink-0">
          <Link to={`/project/${masterProject.id}/createSubProject`}>
            <Plus className="size-4" />
            Crear subproyecto
          </Link>
        </Button>
      </header>
      {subProjects.length === 0 ? (
        <div className="bg-bg-surface border border-border-subtle rounded-xl p-8 flex flex-col items-center gap-3 text-center">
          <p className="text-text-muted text-sm">Esta inmobiliaria aún no tiene subproyectos.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {subProjects.map((sub) => (
            <Link
              key={sub.id}
              to={`/project/${masterProject.id}/prompt/${sub.id}`}
              className="flex items-center justify-between px-5 py-4 bg-bg-surface border border-border-subtle rounded-xl hover:border-brand-vivid hover:shadow-sm transition-all"
            >
              <span className="font-medium text-text-default">
                {sub.name} <span className="text-text-muted text-xs font-normal">({sub.id})</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProjectsView;
